import type { ReactNode } from 'react';
import { fanLayout } from '@/lib/fan';

/**
 * Lays children out as an overlapping arc, the way you hold cards.
 *
 * The critical detail is the nesting. The fan's rotation and lift go on the
 * OUTER span; the caller's `animate-deal-in` sits on the card INSIDE it. Those
 * cannot share an element: that keyframe animates `transform` with
 * `animation-fill-mode: both`, so it holds its final transform forever and would
 * silently overwrite an inline one — the exact shape of the bug where a dimmed
 * card stayed at full brightness because the same animation ended on
 * `opacity: 1`.
 */
export default function FannedHand({
  children,
  cardWidth = 56,
  className = '',
}: {
  children: ReactNode[];
  /** Must match the rendered card width, or the overlap maths is wrong. */
  cardWidth?: number;
  className?: string;
}) {
  const layout = fanLayout(children.length, cardWidth);

  return (
    /*
     * `isolation: isolate` because a hovered card lifts itself to z-50, and the
     * page's sticky header is z-20 — without a stacking context of its own the
     * hand would scroll over the header. Same wall as in TrickTable.
     */
    <div className={`flex justify-center [isolation:isolate] ${className}`}>
      {children.map((child, index) => {
        const card = layout[index];
        return (
          <span
            key={index}
            className="shrink-0 origin-bottom transition-transform duration-150 hover:!z-50 hover:-translate-y-2"
            style={{
              marginLeft: index === 0 ? 0 : -card.overlap,
              transform: `rotate(${card.rotate}deg) translateY(${card.translateY}px)`,
              zIndex: card.zIndex,
            }}
          >
            {child}
          </span>
        );
      })}
    </div>
  );
}
